import {
  ProjectArtifactsService,
  type ProjectArtifactDetailDto,
} from '@inference.engine/workspace-api-client/web';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { PROJECT_ARTIFACT_QUERY_KEYS } from '@/entities/project-artifact';
import { workspaceHttp } from '@/lib/api/http-client';

/**
 * Fetches the enriched single-artifact payload that backs the Library
 * preview modal. The modal's body branches on `artifactKind` and mounts the
 * matching widget/asset renderer — `data`, `description`, and `mimeType` on
 * `ProjectArtifactDetailDto` are the fields each renderer consumes.
 *
 * `enabled` is gated on a non-null `artifactId` so the hook can be called
 * unconditionally from the modal shell (which is mounted once at the
 * Library section level) and stay dormant until an artifact is opened.
 *
 * Cache key routes through `PROJECT_ARTIFACT_QUERY_KEYS.detail` so detail
 * entries share the `['project', projectId, 'artifacts']` prefix with the
 * grid list — a project-scoped invalidation from a future mutation clears
 * both list and detail in one call.
 */
export function useProjectArtifactDetail(
  projectId: string,
  artifactId: string | null,
): UseQueryResult<ProjectArtifactDetailDto, Error> {
  return useQuery({
    // Empty string when disabled: no real artifactId is empty, so the
    // cache key can't collide with any active query. `enabled: false`
    // means queryFn never runs against this key anyway.
    queryKey: PROJECT_ARTIFACT_QUERY_KEYS.detail(projectId, artifactId ?? ''),
    queryFn: async () => {
      const response = await ProjectArtifactsService.getDetail({
        client: workspaceHttp,
        path: { platformProjectId: projectId, artifactId: artifactId as string },
      });

      if (!response.data) {
        throw new Error('Failed to load project artifact detail');
      }

      return response.data;
    },
    enabled: artifactId !== null,
    // Artifact versions are immutable — a row's detail payload never changes
    // once attached. Never refetch, so a detail seeded by the grid's bulk
    // fetch (`useBulkArtifactDetail`) stays a synchronous cache hit when the
    // preview modal opens, with zero network.
    staleTime: Infinity,
  });
}
